import { defineStore } from 'pinia'
import { ref } from 'vue'
import { SubscriptionService } from '@/features/subscription/services/subscription.service'
import type {
  CompanyCountry,
  InvitableUser,
  LoadPlanChangeDetailsCommand,
  PlanChangeCommand,
  PlanChangeDetailsResponse,
  PlanInfo,
  SubscriptionInvoice,
  UpdateCompanyDetailsCommand,
} from '@/features/subscription/types/subscription.types'

export const useSubscriptionStore = defineStore('subscription', () => {
  const service = new SubscriptionService()

  const activating = ref(false)
  const purchasing = ref(false)
  const cancelling = ref(false)
  const updatingPayment = ref(false)
  const invoicesLoading = ref(false)

  const invoices = ref<SubscriptionInvoice[]>([])
  const error = ref<string | null>(null)

  // Current-plan financials (credits, price, billing) for the plan card. Fetched
  // from GetUsers — see PlanInfo docs. Kept separate from the boolean-only
  // SubscriptionDetails so each can load/fail independently.
  const planInfo = ref<PlanInfo | null>(null)
  const planInfoLoading = ref(false)

  // Company-details editing (organic accounts): country options + save state.
  const countries = ref<CompanyCountry[]>([])
  const savingCompany = ref(false)

  // Seat selection (activate / change-plan for unpaid accounts).
  const invitableUsers = ref<InvitableUser[]>([])
  const invitableUsersLoading = ref(false)

  // DEV-6935: details of the plan the user is about to switch to (from the
  // backend), shown in the plan-change confirmation dialog.
  const loadingPlanDetails = ref(false)
  const planChangeDetails = ref<PlanChangeDetailsResponse | null>(null)
  // A request to the sales team about Enterprise is being sent.
  const requestingSales = ref(false)

  let generation = 0

  async function activatePlan(selectedUserIds?: string[]): Promise<string | null> {
    const gen = generation
    activating.value = true
    error.value = null
    try {
      const res = await service.activatePlan(selectedUserIds)
      if (gen !== generation) return null
      // /Subscription/ActivatePlan returns only `{ url }` (no `success` flag, unlike
      // the other redirect endpoints), so gate solely on the presence of the URL —
      // requiring `success` here made the button silently fail on every click.
      if (!res.url) {
        error.value = res.message ?? 'Unable to activate plan.'
        return null
      }
      return res.url
    } catch (e) {
      if (gen === generation) error.value = (e as Error).message
      return null
    } finally {
      if (gen === generation) activating.value = false
    }
  }

  async function purchasePlan(command: PlanChangeCommand): Promise<string | null> {
    const gen = generation
    purchasing.value = true
    error.value = null
    try {
      const res = await service.purchasePlan(command)
      if (gen !== generation) return null
      if (res.isSamePlan) return null
      if (!res.success || !res.url) {
        error.value = res.message ?? 'Unable to start checkout.'
        return null
      }
      return res.url
    } catch (e) {
      if (gen === generation) error.value = (e as Error).message
      return null
    } finally {
      if (gen === generation) purchasing.value = false
    }
  }

  /**
   * DEV-6935: fetch the authoritative details of the target plan (name, credits,
   * price, billing, plan-change message). Stores them in `planChangeDetails` and
   * also returns them; returns null on failure (sets `error`).
   */
  /**
   * Asks the sales team to get in touch (Vue 2 `loadNewPlanDetails`): about an upgrade, or about a
   * downgrade from organic Enterprise. Enterprise changes are sales-assisted, not a checkout.
   */
  async function requestSalesContact(kind: 'upgrade' | 'downgrade'): Promise<boolean> {
    const gen = generation
    requestingSales.value = true
    error.value = null
    try {
      if (kind === 'downgrade') await service.sendDowngradeRequestToSales()
      else await service.sendUpgradeRequestToSales()
      return gen === generation
    } catch {
      if (gen === generation) {
        error.value =
          'Error sending your request to our sales team, please try again or contact support if the issue persists.'
      }
      return false
    } finally {
      if (gen === generation) requestingSales.value = false
    }
  }

  async function loadPlanChangeDetails(
    command: LoadPlanChangeDetailsCommand,
  ): Promise<PlanChangeDetailsResponse | null> {
    const gen = generation
    loadingPlanDetails.value = true
    error.value = null
    planChangeDetails.value = null
    try {
      const res = await service.loadPlanChangeDetails(command)
      if (gen !== generation) return null
      if (!res.success) {
        error.value = 'Unable to load plan details.'
        return null
      }
      planChangeDetails.value = res
      return res
    } catch (e) {
      if (gen === generation) error.value = (e as Error).message
      return null
    } finally {
      if (gen === generation) loadingPlanDetails.value = false
    }
  }

  function clearPlanChangeDetails(): void {
    planChangeDetails.value = null
  }

  async function updatePaymentMethod(): Promise<string | null> {
    const gen = generation
    updatingPayment.value = true
    error.value = null
    try {
      const res = await service.updatePaymentMethod()
      if (gen !== generation) return null
      if (!res.success || !res.url) {
        error.value = res.message ?? 'Unable to update payment method.'
        return null
      }
      return res.url
    } catch (e) {
      if (gen === generation) error.value = (e as Error).message
      return null
    } finally {
      if (gen === generation) updatingPayment.value = false
    }
  }

  async function cancelSubscription(): Promise<boolean> {
    const gen = generation
    cancelling.value = true
    error.value = null
    try {
      const res = await service.cancelSubscription()
      if (gen !== generation) return false
      if (!res.success) {
        error.value = res.message ?? 'Unable to cancel subscription.'
        return false
      }
      return true
    } catch (e) {
      if (gen === generation) error.value = (e as Error).message
      return false
    } finally {
      if (gen === generation) cancelling.value = false
    }
  }

  async function loadPlanInfo(): Promise<void> {
    const gen = generation
    planInfoLoading.value = true
    try {
      const res = await service.getPlanInfo()
      if (gen !== generation) return
      planInfo.value = res
    } catch (e) {
      if (gen === generation) console.warn('Failed to fetch plan info:', (e as Error).message)
    } finally {
      if (gen === generation) planInfoLoading.value = false
    }
  }

  async function loadInvitableUsers(): Promise<void> {
    const gen = generation
    invitableUsersLoading.value = true
    try {
      const users = await service.getInvitableUsers()
      if (gen !== generation) return
      invitableUsers.value = users
    } catch (e) {
      if (gen === generation) {
        invitableUsers.value = []
        error.value = 'Error loading users, please try again or contact support if the issue persists.'
      }
      console.warn('Failed to fetch invitable users:', (e as Error).message)
    } finally {
      if (gen === generation) invitableUsersLoading.value = false
    }
  }

  async function loadCountries(): Promise<void> {
    if (countries.value.length > 0) return
    try {
      countries.value = await service.getCountries()
    } catch (e) {
      console.warn('Failed to fetch countries:', (e as Error).message)
    }
  }

  async function saveCompanyDetails(command: UpdateCompanyDetailsCommand): Promise<boolean> {
    const gen = generation
    savingCompany.value = true
    error.value = null
    try {
      const ok = await service.saveCompanyDetails(command)
      if (gen !== generation) return false
      if (!ok) {
        error.value = 'Error saving company details, please try again or contact support if the issue persists.'
        return false
      }
      // Reflect the saved values locally so the card updates without a refetch.
      if (planInfo.value) {
        planInfo.value = {
          ...planInfo.value,
          companyName: command.CompanyName,
          companyWebsite: command.CompanyWebsite,
          companyCountryId: command.CountryDetail.Id,
          companyCountryName: command.CountryDetail.Name,
        }
      }
      return true
    } catch (e) {
      if (gen === generation) error.value = (e as Error).message
      return false
    } finally {
      if (gen === generation) savingCompany.value = false
    }
  }

  async function loadInvoices(): Promise<void> {
    const gen = generation
    invoicesLoading.value = true
    error.value = null
    try {
      const res = await service.getInvoices()
      if (gen !== generation) return
      invoices.value = res.success ? res.invoices : []
      if (!res.success) error.value = 'Unable to load invoices.'
    } catch (e) {
      if (gen === generation) error.value = (e as Error).message
    } finally {
      if (gen === generation) invoicesLoading.value = false
    }
  }

  function $reset() {
    generation++
    activating.value = false
    purchasing.value = false
    cancelling.value = false
    updatingPayment.value = false
    invoicesLoading.value = false
    loadingPlanDetails.value = false
    requestingSales.value = false
    planInfoLoading.value = false
    savingCompany.value = false
    invitableUsersLoading.value = false
    invoices.value = []
    countries.value = []
    invitableUsers.value = []
    planChangeDetails.value = null
    planInfo.value = null
    error.value = null
  }

  return {
    activating, purchasing, cancelling, updatingPayment, invoicesLoading, loadingPlanDetails,
    requestingSales, planInfoLoading, savingCompany, invitableUsersLoading,
    invoices, countries, invitableUsers, planChangeDetails, planInfo, error,
    activatePlan, purchasePlan, loadPlanChangeDetails, clearPlanChangeDetails, requestSalesContact,
    updatePaymentMethod, cancelSubscription, loadInvoices, loadPlanInfo,
    loadCountries, saveCompanyDetails, loadInvitableUsers,
    $reset,
  }
})
